import { useMemo } from 'react';

import type { SearchDocument } from '@/features/AudioPlayer';

import { useContributorSlugsByName } from './useContributorSlugByName';

// sfx.v3 documents only ship `contributor_name`, not `contributor_slug`,
// so every SFX listing surface needs to look up slugs from `contributors.v1`
// before passing rows to `VirtualizedTrackList` (which renders the
// contributor name as a link when a slug is present). This hook centralises
// that enrichment: take a list of normalised SFX results, return the same
// list with `contributor_slug` populated wherever the name resolved.
export const useSfxResultsWithContributorSlugs = (
  results: SearchDocument[]
): SearchDocument[] => {
  const contributorNames = useMemo(
    () =>
      results.map((r) => r.contributor_name ?? '').filter((n) => n.length > 0),
    [results]
  );

  const slugMap = useContributorSlugsByName(contributorNames);

  // Names array is memoised against `results`; the upstream call sites in
  // sfx/*.page.tsx wrap their own results in useMemo against the typesense
  // payload, so the slug-lookup query key only invalidates when the actual
  // SFX rows change.
  return useMemo(
    () =>
      results.map((r) => ({
        ...r,
        // `||` (not `??`) is deliberate: sfx.v3 sometimes stores an empty
        // string for contributor_slug, which should still fall through to
        // the resolved slugMap rather than be treated as a real value.
        contributor_slug:
          r.contributor_slug ||
          (r.contributor_name ? slugMap[r.contributor_name] : undefined),
      })),
    [results, slugMap]
  );
};
